import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Box, Button, TextField, Typography, useMediaQuery, useTheme,} from '@mui/material';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {useKeycloak} from '../providers/KeycloakProvider';
import {useLogin} from '../hooks/useLogin';
import LoadingScreen from '../components/Common/LoadingScreen';
import {AnalyticsService} from '../services/analyticsService';

export const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const { isAuthenticated, isAuthenticating } = useKeycloak();
    const { login, loading, error, clearError } = useLogin();

    // Check for logout reason in URL parameters
    const logoutReason = searchParams.get('reason');
    const getLogoutMessage = () => {
        switch (logoutReason) {
            case 'inactivity':
                return 'Zostałeś automatycznie wylogowany z powodu bezczynności. Zaloguj się ponownie.';
            case 'session_expired':
                return 'Twoja sesja wygasła. Zaloguj się ponownie.';
            case 'token_expired':
                return 'Token uwierzytelnienia wygasł. Zaloguj się ponownie.';
            default:
                return null;
        }
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isDemoHost = (() => {
        try {
            return typeof window !== 'undefined' && window.location.hostname === 'demo.ustalone.com';
        } catch {
            return false;
        }
    })();

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    // Autofill credentials for demo environment when accessed via demo.ustalone.com?demo=true
    useEffect(() => {
        try {
            const isDemoParam = (searchParams.get('demo') || '').toLowerCase() === 'true';
            if (isDemoParam) {
                setEmail('demo');
                setPassword('Demo123!');
            }
        } catch (e) {
            // no-op: defensively ignore in non-browser or unexpected environments
        }
    }, []);

    const logoSvg = useMemo(() => (
        <svg
            width="56"
            height="56"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <defs>
                <linearGradient id="ustalone-logo-grad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#06b6d4" />
                    <stop offset="1" stopColor="#2563eb" />
                </linearGradient>
            </defs>
            <circle cx="20" cy="20" r="18" fill="url(#ustalone-logo-grad)" />
            <path
                d="M14 21.5L19 26L27 16"
                stroke="white"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    ), []);

    if (isAuthenticating) {
        return <LoadingScreen message="Weryfikacja tożsamości..." />;
    }

    const validateForm = () => {
        let valid = true;
        setPasswordError('');

        if (!password.trim()) {
            setPasswordError('Hasło jest wymagane');
            valid = false;
        } else if (password.length < 6) {
            setPasswordError('Hasło musi mieć co najmniej 6 znaków');
            valid = false;
        }

        return valid;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validateForm()) return;

        // Track login button click event with form data (excluding password)
        await AnalyticsService.trackLoginClick({
            email: email
        });

        // Clear any previous errors
        clearError();

        try {
            const success = await login({ username: email, password });
            
            if (success) {
                // Przekierowanie po udanym logowaniu
                const redirectPath = sessionStorage.getItem('redirect_after_login') || '/';
                sessionStorage.removeItem('redirect_after_login');
                navigate(redirectPath);
            }
        } catch (err) {
            console.error('Błąd logowania:', err);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                minHeight: '100vh',
            }}
        >
            {/* LEFT COLUMN */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    px: { xs: 4, md: 8 },
                    py: 6,
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 480 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                        {logoSvg}
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '1.5rem',
                                color: 'primary.main',
                            }}
                        >
                            ustalone
                        </Typography>
                    </Box>

                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 5}}>
                        Logowanie
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 4 }}>
                        <strong>Ustalone</strong> to <strong>nowoczesny system CRM</strong>, który pomaga zarządzać relacjami z klientami, automatyzować sprzedaż i rozwijać Twój biznes. Zaloguj się, aby w pełni wykorzystać jego możliwości.
                    </Typography>

                    <form onSubmit={handleSubmit} noValidate>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        {getLogoutMessage() && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {getLogoutMessage()}
                            </Alert>
                        )}
                        <TextField
                            label="Login/email"
                            type="text"
                            fullWidth
                            required
                            variant="standard"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            sx={{ mb: 2 }}
                            helperText={isDemoHost ? 'Podpowiedź: login - demo' : undefined}
                        />
                        <TextField
                            label="Hasło"
                            type="password"
                            fullWidth
                            required
                            variant="standard"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            sx={{ mb: 3 }}
                            error={!!passwordError}
                            helperText={passwordError || (isDemoHost ? 'Podpowiedź: hasło - Demo123!' : undefined)}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                            <Button type="submit" variant="contained" disabled={loading}>
                                {loading ? 'Logowanie...' : 'Zaloguj się'}
                            </Button>
                        </Box>
                    </form>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
                        <Link to="/reset-password-request" style={{ color: 'inherit', textDecoration: 'underline', fontSize: '0.875rem' }}>
                            Zapomniałem hasła
                        </Link>
                    </Box>
                </Box>

                <Typography variant="caption" color="text.secondary">
                    Copyright © 2025 Devlab Sp. z o.o.
                </Typography>
            </Box>

            {/* RIGHT COLUMN - Tylko obrazek */}
            {!isMobile && (
                <Box
                    sx={{
                        flex: 1,
                        backgroundImage: 'url(/crm-login.webp)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
            )}
        </Box>
    );
};

export default LoginPage;
